<template>
  <AdminShell title="openFDA 导入">
    <div class="adm-import-steps">
      <a-steps :current="stepIndex" size="small">
        <a-step title="选择文件" />
        <a-step title="上传创建任务" />
        <a-step title="后台导入" />
        <a-step title="完成" />
      </a-steps>
    </div>

    <div class="adm-import-panel">
      <template v-if="step === 'upload'">
        <a-upload-dragger accept=".json,application/json" :show-upload-list="false" :before-upload="handleBeforeUpload">
          <p class="ant-upload-drag-icon"><Upload :size="36" /></p>
          <p class="ant-upload-text">点击或拖拽 JSON 文件到此处</p>
          <p class="adm-cell-muted" style="margin-top:8px">
            openFDA 官方 .json，最大 1024MB；文件名建议包含 510k / pma / registration|listing
          </p>
        </a-upload-dragger>
      </template>

      <template v-else-if="step === 'confirm'">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom:16px"
          :message="`待上传：${selectedFileName}`"
          :description="`大小 ${formatFileSize(selectedFileSize)}${previewDataType ? ` · 预估类型 ${previewDataType}` : ' · 类型将由服务端识别'}`"
        />
        <a-space>
          <a-button type="primary" @click="startImport">开始上传并导入</a-button>
          <a-button @click="resetUpload">重新选择</a-button>
        </a-space>
      </template>

      <template v-else-if="step === 'uploading'">
        <p><strong>{{ selectedFileName }}</strong></p>
        <a-progress :percent="Math.floor(uploadProgress)" status="active" />
        <p class="adm-cell-muted">正在上传文件… {{ Math.floor(uploadProgress) }}%（仅文件传输进度）</p>
      </template>

      <template v-else-if="step === 'processing'">
        <p><strong>{{ selectedFileName }}</strong></p>
        <p class="adm-cell-muted" style="margin-bottom:8px">
          任务号：{{ taskNo }} · 状态：{{ statusLabel(taskStatus) }}
        </p>
        <a-progress :percent="processPercent" status="active" />
        <a-row :gutter="16" style="margin-top:16px">
          <a-col :span="6"><a-statistic title="源记录" :value="taskSnapshot.total_count || 0" /></a-col>
          <a-col :span="6"><a-statistic title="已处理" :value="taskSnapshot.processed_count || 0" /></a-col>
          <a-col :span="6"><a-statistic title="成功" :value="taskSnapshot.success_count || 0" /></a-col>
          <a-col :span="6"><a-statistic title="失败" :value="taskSnapshot.failure_count || 0" /></a-col>
        </a-row>
        <p class="adm-cell-muted" style="margin-top:12px">
          {{ processHint }}
        </p>
      </template>

      <template v-else-if="step === 'done'">
        <a-result
          :status="resultStatus"
          :title="resultTitle"
          :sub-title="resultSubTitle"
        >
          <template #extra>
            <a-row :gutter="24" justify="center" style="margin-bottom:16px">
              <a-col><a-statistic title="源记录" :value="taskSnapshot.total_count || 0" /></a-col>
              <a-col><a-statistic title="成功" :value="taskSnapshot.success_count || 0" /></a-col>
              <a-col><a-statistic title="跳过" :value="taskSnapshot.ignored_count || 0" /></a-col>
              <a-col><a-statistic title="失败" :value="taskSnapshot.failure_count || 0" /></a-col>
            </a-row>
            <p v-if="taskSnapshot.error_message" class="adm-import-error-msg">
              {{ taskSnapshot.error_message }}
            </p>
            <div v-if="taskSnapshot.error_details?.length" class="adm-import-errors">
              <p class="adm-cell-muted">错误明细（最多展示 10 条）：</p>
              <ul>
                <li v-for="(err, idx) in taskSnapshot.error_details.slice(0, 10)" :key="idx">
                  {{ formatError(err) }}
                </li>
              </ul>
            </div>
            <a-button type="primary" @click="resetUpload">继续导入</a-button>
          </template>
        </a-result>
      </template>
    </div>

    <div class="adm-api-section" style="margin-top:24px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
        <h3 class="adm-api-section-title" style="margin:0">导入任务</h3>
        <a-button size="small" :loading="historyLoading" @click="loadHistory">刷新</a-button>
      </div>
      <a-table
        class="adm-table"
        :columns="historyColumns"
        :data-source="historyItems"
        :loading="historyLoading"
        :pagination="false"
        row-key="task_no"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag>
          </template>
          <template v-else-if="column.key === 'progress'">
            {{ formatTaskProgress(record) }}
          </template>
          <template v-else-if="column.key === 'create_time'">
            {{ formatTime(record.create_time) }}
          </template>
        </template>
      </a-table>
      <div class="table-pagination">
        <div class="pagination-left">
          <div class="page-size-selector">
            <span class="page-size-text">每页</span>
            <a-select
              :value="historyLimit"
              size="small"
              class="page-size-select"
              @change="onHistoryPageSizeChange"
            >
              <a-select-option :value="10">10</a-select-option>
              <a-select-option :value="20">20</a-select-option>
              <a-select-option :value="50">50</a-select-option>
              <a-select-option :value="100">100</a-select-option>
            </a-select>
            <span class="page-size-text">条</span>
          </div>
          <span class="total-text">共 {{ historyTotal.toLocaleString() }} 条</span>
        </div>
        <a-pagination
          :current="historyPage"
          :total="historyTotal"
          :page-size="historyLimit"
          :show-size-changer="false"
          :show-quick-jumper="false"
          class="pagination-component"
          @change="onHistoryPageChange"
        />
      </div>
    </div>
  </AdminShell>
</template>

<script>
import { message } from 'ant-design-vue'
import { Upload } from 'lucide-vue-next'
import AdminShell from '@/components/admin/AdminShell.vue'
import {
  importOpenFdaFile,
  getOpenFdaImportTask,
  getOpenFdaImportTasks,
} from '@/api/adminApi'

const MAX_FILE_BYTES = 1024 * 1024 * 1024
const POLL_INTERVAL_MS = 3000
const TERMINAL_STATUS = new Set(['success', 'partial_success', 'failed'])
const STEP = {
  upload: 0,
  confirm: 0,
  uploading: 1,
  processing: 2,
  done: 3,
}

const STATUS_META = {
  pending: { label: '等待处理', color: 'default' },
  splitting: { label: '拆分中', color: 'processing' },
  processing: { label: '写入中', color: 'processing' },
  success: { label: '成功', color: 'success' },
  partial_success: { label: '部分成功', color: 'warning' },
  failed: { label: '失败', color: 'error' },
}

function detectDataType(fileName) {
  const n = String(fileName || '').toLowerCase()
  if (n.includes('510k') || n.includes('510(k)')) return '510k'
  if (n.includes('pma') || n.includes('pre-market') || n.includes('premarket')) return 'pma'
  if (n.includes('registration') || n.includes('listing') || n.includes('reglist')) return 'registration'
  return ''
}

function formatDuration(ms) {
  const sec = Math.max(0, Math.round(ms / 1000))
  if (sec < 60) return `${sec} 秒`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m} 分 ${s} 秒`
}

function emptyTask() {
  return {
    task_no: '',
    file_name: '',
    data_type: null,
    status: '',
    total_count: 0,
    processed_count: 0,
    success_count: 0,
    ignored_count: 0,
    failure_count: 0,
    error_message: null,
    error_details: [],
    start_time: null,
    finish_time: null,
  }
}

export default {
  name: 'ImportView',
  components: { Upload, AdminShell },
  data() {
    return {
      step: 'upload',
      selectedFile: null,
      selectedFileName: '',
      selectedFileSize: 0,
      previewDataType: '',
      uploadProgress: 0,
      taskNo: '',
      taskStatus: '',
      taskSnapshot: emptyTask(),
      pollTimer: null,
      startedAt: 0,
      historyLoading: false,
      historyItems: [],
      historyPage: 1,
      historyLimit: 10,
      historyTotal: 0,
      historyColumns: [
        { title: '任务号', dataIndex: 'task_no', key: 'task_no', width: 220, ellipsis: true },
        { title: '文件名', dataIndex: 'file_name', key: 'file_name', ellipsis: true },
        { title: '类型', dataIndex: 'data_type', key: 'data_type', width: 140 },
        { title: '状态', key: 'status', width: 100 },
        { title: '进度', key: 'progress', width: 180 },
        { title: '成功', dataIndex: 'success_count', key: 'success_count', width: 80 },
        { title: '失败', dataIndex: 'failure_count', key: 'failure_count', width: 80 },
        { title: '创建时间', key: 'create_time', width: 168 },
      ],
    }
  },
  computed: {
    stepIndex() {
      return STEP[this.step] ?? 0
    },
    processPercent() {
      const total = Number(this.taskSnapshot.total_count) || 0
      const processed = Number(this.taskSnapshot.processed_count) || 0
      if (total <= 0) {
        if (this.taskStatus === 'pending') return 5
        if (this.taskStatus === 'splitting') return 15
        return 25
      }
      return Math.min(99, Math.max(1, Math.round((processed / total) * 100)))
    },
    processHint() {
      const total = Number(this.taskSnapshot.total_count) || 0
      if (!total) {
        if (this.taskStatus === 'pending') return '任务已创建，等待后台开始处理…'
        if (this.taskStatus === 'splitting') return '正在解析并拆分大文件，请稍候…'
        return '后台处理中…'
      }
      return `导入进度 ${this.taskSnapshot.processed_count || 0} / ${total}`
    },
    resultStatus() {
      if (this.taskStatus === 'failed') return 'error'
      if (this.taskStatus === 'partial_success') return 'warning'
      return 'success'
    },
    resultTitle() {
      if (this.taskStatus === 'failed') return '导入失败'
      if (this.taskStatus === 'partial_success') return '导入完成（部分失败）'
      return '导入完成'
    },
    resultSubTitle() {
      const type = this.taskSnapshot.data_type || this.previewDataType || '未知'
      const duration = this.startedAt ? formatDuration(Date.now() - this.startedAt) : '—'
      return `任务 ${this.taskNo} · 类型 ${type} · 耗时 ${duration}`
    },
  },
  mounted() {
    this.loadHistory()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    statusLabel(status) {
      return STATUS_META[status]?.label || status || '—'
    },
    statusColor(status) {
      return STATUS_META[status]?.color || 'default'
    },
    formatFileSize(bytes) {
      const n = Number(bytes) || 0
      if (n < 1024) return `${n} B`
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
      if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
      return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
    },
    formatTime(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString('zh-CN')
      } catch {
        return iso
      }
    },
    formatError(err) {
      if (typeof err === 'string') return err
      if (err && typeof err === 'object') {
        const idx = err.source_index != null ? `#${err.source_index} ` : ''
        return `${idx}${err.message || err.errmsg || JSON.stringify(err)}`
      }
      return String(err)
    },
    formatTaskProgress(record) {
      const total = Number(record?.total_count) || 0
      const processed = Number(record?.processed_count) || 0
      if (!total) return this.statusLabel(record?.status)
      return `${processed}/${total}`
    },
    async loadHistory() {
      this.historyLoading = true
      try {
        const res = await getOpenFdaImportTasks({ page: this.historyPage, limit: this.historyLimit })
        const data = res.data || {}
        this.historyItems = data.items || []
        this.historyTotal = data.total || 0
        this.historyPage = data.page || this.historyPage
        this.historyLimit = data.limit || this.historyLimit
      } catch {
        this.historyItems = []
        this.historyTotal = 0
      } finally {
        this.historyLoading = false
      }
    },
    onHistoryPageChange(page) {
      this.historyPage = page
      this.loadHistory()
    },
    onHistoryPageSizeChange(size) {
      this.historyLimit = size
      this.historyPage = 1
      this.loadHistory()
    },
    handleBeforeUpload(file) {
      this.selectFile(file)
      return false
    },
    selectFile(file) {
      if (!file) return
      const name = file.name || ''
      if (!/\.json$/i.test(name)) {
        message.error('仅支持 .json 文件')
        return
      }
      if (!file.size) {
        message.error('上传文件不能为空')
        return
      }
      if (file.size > MAX_FILE_BYTES) {
        message.error('上传文件不能超过 1024MB')
        return
      }

      this.selectedFile = file
      this.selectedFileName = name
      this.selectedFileSize = file.size
      this.previewDataType = detectDataType(name)
      this.step = 'confirm'
    },
    async startImport() {
      if (!this.selectedFile) {
        message.warning('请先选择文件')
        return
      }

      this.stopPolling()
      this.step = 'uploading'
      this.uploadProgress = 0
      this.taskNo = ''
      this.taskStatus = ''
      this.taskSnapshot = emptyTask()
      this.startedAt = Date.now()

      try {
        const body = await importOpenFdaFile(this.selectedFile, {
          onUploadProgress: (evt) => {
            if (!evt.total) return
            this.uploadProgress = Math.min(99, Math.round((evt.loaded / evt.total) * 100))
          },
        })
        this.uploadProgress = 100

        const data = body?.data || {}
        const taskNo = data.task_no
        if (!taskNo) {
          throw new Error('未返回 task_no，无法查询导入进度')
        }

        this.taskNo = taskNo
        this.applyTaskData(data)
        this.step = 'processing'
        message.success(body?.errmsg || '任务已创建，开始后台导入')
        this.startPolling()
        this.historyPage = 1
        this.loadHistory()
      } catch (e) {
        message.error(e.message || '上传失败')
        this.step = 'confirm'
        this.uploadProgress = 0
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        this.pollTaskOnce()
      }, POLL_INTERVAL_MS)
      this.pollTaskOnce()
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async pollTaskOnce() {
      if (!this.taskNo) return
      try {
        const res = await getOpenFdaImportTask(this.taskNo)
        const data = res.data || {}
        this.applyTaskData(data)

        if (TERMINAL_STATUS.has(data.status)) {
          this.stopPolling()
          this.step = 'done'
          if (data.status === 'failed') {
            message.error(data.error_message || '导入失败')
          } else if (data.status === 'partial_success') {
            message.warning('导入完成，存在部分失败记录')
          } else {
            message.success('导入完成')
          }
          this.loadHistory()
        }
      } catch (e) {
        // 轮询失败不立刻中断，等待下次；连续失败由用户刷新任务列表查看
        console.warn('poll import task failed', e)
      }
    },
    applyTaskData(data) {
      this.taskSnapshot = {
        ...emptyTask(),
        ...data,
        error_details: Array.isArray(data?.error_details) ? data.error_details : [],
      }
      this.taskStatus = data?.status || ''
      if (data?.data_type) {
        this.previewDataType = data.data_type
      }
    },
    resetUpload() {
      this.stopPolling()
      this.step = 'upload'
      this.selectedFile = null
      this.selectedFileName = ''
      this.selectedFileSize = 0
      this.previewDataType = ''
      this.uploadProgress = 0
      this.taskNo = ''
      this.taskStatus = ''
      this.taskSnapshot = emptyTask()
      this.startedAt = 0
    },
  },
}
</script>

<style scoped>
.adm-import-error-msg {
  color: #cf1322;
  margin-bottom: 12px;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
  word-break: break-word;
}
.adm-import-errors {
  text-align: left;
  max-width: 640px;
  margin: 0 auto 16px;
}
.adm-import-errors ul {
  margin: 0;
  padding-left: 18px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
}
.table-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 0;
}
.pagination-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}
.page-size-text,
.total-text {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}
.page-size-select {
  width: 80px;
}
.pagination-component {
  display: flex;
  align-items: center;
}
</style>
